import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { createApiContext } from "../../../shared/api/create-api-context";
import type { NutritionPlanPayload } from "../model/plans";
import type { Anamnesis, AnamnesisInput, NutritionApi, NutritionPlan } from "./nutrition-api";

const context = createApiContext<NutritionApi>("Nutrition");
export const NutritionApiProvider = context.Provider;
export const useNutritionApi = context.useApi;

export const nutritionKeys = {
  all: (profileId: string) => ["nutrition", profileId] as const,
  plans: (profileId: string, page: number) =>
    [...nutritionKeys.all(profileId), "plans", page] as const,
  anamnesis: (profileId: string) => [...nutritionKeys.all(profileId), "anamnesis"] as const,
};

export function useNutritionPlans(profileId: string, page = 0): UseQueryResult<NutritionPlan[]> {
  const api = useNutritionApi();
  return useQuery({
    queryKey: nutritionKeys.plans(profileId, page),
    queryFn: ({ signal }) => api.plans(profileId, page, signal),
    enabled: profileId !== "",
  });
}

export function useAnamnesis(profileId: string): UseQueryResult<Anamnesis | null> {
  const api = useNutritionApi();
  return useQuery({
    queryKey: nutritionKeys.anamnesis(profileId),
    queryFn: ({ signal }) => api.anamnesis(profileId, signal),
    enabled: profileId !== "",
  });
}

export function useSaveNutritionPlan(profileId: string) {
  const api = useNutritionApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { payload: NutritionPlanPayload; baseId: string | null }) =>
      api.savePlan(profileId, input.payload, input.baseId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: nutritionKeys.all(profileId) });
    },
  });
}

export function useChangeNutritionPlanStatus(profileId: string) {
  const api = useNutritionApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { planId: string; status: string }) =>
      api.changeStatus(input.planId, input.status),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: nutritionKeys.all(profileId) });
    },
  });
}

export function useDeleteNutritionPlan(profileId: string) {
  const api = useNutritionApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => api.deletePlan(planId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: nutritionKeys.all(profileId) });
    },
  });
}

export function useSaveAnamnesis(userId: string, profileId: string) {
  const api = useNutritionApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AnamnesisInput) => api.saveAnamnesis(userId, input),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: nutritionKeys.anamnesis(profileId) });
    },
  });
}

export function useDeleteAnamnesis(profileId: string) {
  const api = useNutritionApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (anamnesisId: string) => api.deleteAnamnesis(anamnesisId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: nutritionKeys.anamnesis(profileId) });
    },
  });
}
